import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Heart,
  MessageCircle,
  Share2,
  Sparkles,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Flag,
  Ban,
  Pencil,
  Trash2,
  Loader2,
  Pin,
  PinOff
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ReportDialog from '../ReportDialog';
import BlockUserDialog from '../BlockUserDialog';
import ShareButtons from '../ShareButtons';
import LinkPreviewCard from '../LinkPreviewCard';
import { TagList } from '../TagBadge';
import BetaBadge from '../BetaBadge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCategoryColor = (category) => {
  const colors = {
    'general': 'bg-muted text-muted-foreground',
    'care-tips': 'bg-primary/10 text-primary',
    'identification': 'bg-info/10 text-info',
    'showcase': 'bg-secondary/10 text-secondary',
    'question': 'bg-warning/10 text-warning',
    'news': 'bg-accent text-accent-foreground'
  };
  return colors[category] || colors.general;
};

export function PostCard({
  post,
  user,
  userVote,
  isLiked,
  onVote,
  onLike,
  onBlock,
  onRefresh
}) {
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinDuration, setPinDuration] = useState('7d');
  const [editForm, setEditForm] = useState({ title: post.title, content: post.content });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pinning, setPinning] = useState(false);

  const isOwner = user && user.id === post.author_id;
  const isAdmin = user && (user.is_admin || user.is_sysadmin || user.is_operator_admin);

  const handleNavigateToPost = () => {
    navigate(`/post/${post.id}`);
  };

  const handleNavigateToProfile = (e) => {
    e.stopPropagation();
    navigate(`/profile/${post.author_username}`);
  };

  const handlePinPost = async (duration = '7d') => {
    setPinning(true);
    try {
      if (post.is_pinned) {
        // Unpin
        const response = await fetch(`${API}/posts/${post.id}/pin`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          toast.success('Post unpinned');
          onRefresh?.();
        } else {
          toast.error('Failed to unpin');
        }
      } else {
        // Pin with duration
        const response = await fetch(`${API}/posts/${post.id}/pin?duration=${duration}`, {
          method: 'POST',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          let message = 'Post pinned to top';
          if (data.unpinned_post) {
            message += ` (auto-unpinned: "${data.unpinned_post}")`;
          }
          toast.success(message);
          onRefresh?.();
        } else {
          toast.error('Failed to pin post');
        }
      }
      setShowPinDialog(false);
    } catch (error) {
      toast.error('Failed to update pin status');
    } finally {
      setPinning(false);
    }
  };

  const handleEditPost = async () => {
    if (!editForm.title.trim() || !editForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API}/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ title: editForm.title, content: editForm.content })
      });
      if (response.ok) {
        toast.success('Post updated');
        setShowEditDialog(false);
        onRefresh?.();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update post');
      }
    } catch (error) {
      toast.error('Error updating post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/posts/${post.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        toast.success('Post deleted');
        setShowDeleteDialog(false);
        onRefresh?.();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete post');
      }
    } catch (error) {
      toast.error('Error deleting post');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card 
      className="card-interactive cursor-pointer"
      onClick={handleNavigateToPost}
      data-testid={`post-card-${post.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar 
              className="w-10 h-10 cursor-pointer" 
              onClick={handleNavigateToProfile}
            >
              <AvatarImage src={post.author_avatar} />
              <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p 
                className="font-medium hover:underline cursor-pointer flex items-center gap-1"
                onClick={handleNavigateToProfile}
              >
                @{post.author_username}
                {post.author_is_beta_user && <BetaBadge size="md" />}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {post.is_pinned && (
              <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0" data-testid={`pinned-badge-${post.id}`}>
                <Pin className="w-3 h-3 mr-1" />
                Pinned
              </Badge>
            )}
            <Badge className={getCategoryColor(post.category)}>
              {post.category}
            </Badge>
            {post.is_trending && (
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0" data-testid={`trending-badge-${post.id}`}>
                <Sparkles className="w-3 h-3 mr-1" />
                Trending
              </Badge>
            )}
            {/* Post actions menu - shows for all logged in users */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`post-menu-${post.id}`}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {/* Admin actions: Pin/Unpin */}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => post.is_pinned ? handlePinPost() : setShowPinDialog(true)}
                        disabled={pinning}
                        data-testid={`pin-post-${post.id}`}
                      >
                        {post.is_pinned ? (
                          <>
                            <PinOff className="w-4 h-4 mr-2" />
                            Unpin Post
                          </>
                        ) : (
                          <>
                            <Pin className="w-4 h-4 mr-2" />
                            Pin to Top...
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {/* Owner actions: Edit/Delete */}
                  {isOwner && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => {
                          setEditForm({ title: post.title, content: post.content });
                          setShowEditDialog(true);
                        }}
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Post
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setShowDeleteDialog(true)}
                        className="text-destructive focus:text-destructive"
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Post
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {/* Non-owner actions: Report/Block */}
                  {!isOwner && (
                    <>
                      <ReportDialog
                        contentType="post"
                        contentId={post.id}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`report-post-${post.id}`}>
                            <Flag className="w-4 h-4 mr-2" />
                            Report Post
                          </DropdownMenuItem>
                        }
                      />
                      <BlockUserDialog
                        userId={post.author_id}
                        username={post.author_username}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`block-user-${post.id}`}>
                            <Ban className="w-4 h-4 mr-2" />
                            Block @{post.author_username}
                          </DropdownMenuItem>
                        }
                        onBlocked={() => {
                          onBlock?.(post.author_id);
                          toast.success(`Posts from @${post.author_username} hidden`);
                        }}
                      />
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-2">
            <TagList 
              tags={post.tags} 
              size="xs"
              clickable
              onTagClick={(tag) => {
                toast.info(`Filter by "${tag}" coming soon!`);
              }}
            />
          </div>
        )}

        <h3 className="font-heading text-lg font-medium mb-2">{post.title}</h3>
        <p className="text-muted-foreground line-clamp-3 mb-4">{post.content}</p>
        
        {/* Link Preview */}
        {post.link_preview && (
          <div onClick={(e) => e.stopPropagation()}>
            <LinkPreviewCard preview={post.link_preview} className="mb-4" />
          </div>
        )}

        {post.image_url && post.image_url.trim() !== '' && (
          <div className="rounded-lg overflow-hidden mb-4">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full h-64 object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        <div className="flex items-center gap-6 pt-3 border-t">
          {/* Voting */}
          <div className="flex items-center gap-1">
            <button
              className={`p-1 rounded transition-colors ${
                userVote === 'up' 
                  ? 'text-primary bg-primary/10' 
                  : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
              }`}
              onClick={(e) => onVote(post.id, 'up', e)}
              data-testid={`upvote-btn-${post.id}`}
            >
              <ChevronUp className="w-5 h-5" />
            </button>
            <span className={`text-sm font-medium min-w-[2rem] text-center ${
              (post.score || 0) > 0 ? 'text-primary' : 
              (post.score || 0) < 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {post.score || 0}
            </span>
            <button
              className={`p-1 rounded transition-colors ${
                userVote === 'down' 
                  ? 'text-destructive bg-destructive/10' 
                  : 'text-muted-foreground hover:text-destructive hover:bg-destructive/5'
              }`}
              onClick={(e) => onVote(post.id, 'down', e)}
              data-testid={`downvote-btn-${post.id}`}
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          <button 
            className={`flex items-center gap-2 text-sm transition-colors ${
              isLiked ? 'text-secondary' : 'text-muted-foreground hover:text-secondary'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onLike(post.id);
            }}
            data-testid={`like-btn-${post.id}`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            {post.likes_count}
          </button>
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <MessageCircle className="w-4 h-4" />
            {post.comments_count}
          </button>
          {/* Share count indicator */}
          {(post.share_count || 0) > 0 && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground" data-testid={`share-count-${post.id}`}>
              <Share2 className="w-4 h-4" />
              {post.share_count}
            </span>
          )}
          <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
            <ShareButtons
              url={`/post/${post.id}`}
              title={post.title}
              description={post.content?.substring(0, 100)}
              source="feed"
              compact={true}
              showRepost={!!user}
              onRepost={async (comment) => {
                const response = await fetch(`${API}/shares/repost`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    original_post_id: post.id,
                    comment
                  })
                });
                if (!response.ok) throw new Error('Failed to repost');
                onRefresh?.();
              }}
            />
          </div>
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Input
                placeholder="Title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div>
              <Textarea
                placeholder="Content"
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditPost} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePost} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pin Duration Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pin className="w-5 h-5" />
              Pin Post
            </DialogTitle>
            <DialogDescription>
              Choose how long to pin this post to the top of the feed. Max 3 pinned posts allowed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            {[
              { value: '24h', label: '24 Hours', desc: 'For time-sensitive announcements' },
              { value: '7d', label: '7 Days', desc: 'Default - good for featured content' },
              { value: '30d', label: '30 Days', desc: 'For ongoing campaigns' },
              { value: 'indefinite', label: 'Indefinite', desc: 'Until manually unpinned' },
            ].map((option) => (
              <label 
                key={option.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  pinDuration === option.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted hover:bg-muted/50'
                }`}
              >
                <input
                  type="radio"
                  name="pinDuration"
                  value={option.value}
                  checked={pinDuration === option.value}
                  onChange={(e) => setPinDuration(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinDialog(false)} disabled={pinning}>
              Cancel
            </Button>
            <Button onClick={() => handlePinPost(pinDuration)} disabled={pinning}>
              {pinning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
              Pin Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PostCard;
